import { isHardcodedPronunciationReading } from "#hardcoded-pronunciation-levels";
import type { GraphemeLessonPart } from "#level";

/** A character reading's syllabus placement, as its content-DB pronunciation card carries it. */
export type GraphemeReadingPlacement = {
  /** The reading's HSK 2026 level (`7-9` → 7); null when the syllabus does not list it. */
  hsk2026Level: number | null;
  /** The reading introduced together with the character (the lowest-level one). */
  isMain: boolean;
  /** The reading's TBCL level; null when the syllabus does not list it. */
  tbclLevel: number | null;
};

/**
 * Whether a lesson of `part` introduces this reading of `grapheme`. A meaning-only lesson
 * (`part: "meaning"`, Levels 1–3) introduces none — those readings come later from a pronunciation
 * level (`part: "pronunciation"`). Otherwise the main reading always comes with the character,
 * hardcoded P-level readings (e.g. 干 gan4 on 2-P1) are force-introduced on pronunciation lessons
 * ahead of their syllabus grade, and any other reading comes once the script's syllabus (HSK for
 * simplified, TBCL for traditional) places it at or below `curriculumGrade`. A higher reading is
 * introduced later, by the first word that uses it.
 *
 * When `onlyPinyin` is set (a single-reading pronunciation lesson — 干 gan4, then 干 gan1), only
 * that reading is introduced even if other readings of the grapheme would otherwise qualify.
 */
export function isGraphemeReadingIntroducedAtGrade({
  curriculumGrade,
  grapheme,
  onlyPinyin,
  part,
  pinyin,
  reading,
  script,
}: {
  curriculumGrade: number | null;
  grapheme: string;
  /** Restrict this lesson to one reading (hardcoded opening slots that share a grapheme). */
  onlyPinyin?: string;
  part: GraphemeLessonPart;
  pinyin: string;
  reading: GraphemeReadingPlacement;
  script: "simplified" | "traditional";
}): boolean {
  // Meaning-only levels teach the gloss alone; pronunciations are separate 2-P1 cards.
  if (part === "meaning" || part === "primitive") {
    return false;
  }
  if (onlyPinyin !== undefined && pinyin !== onlyPinyin) {
    return false;
  }
  if (isHardcodedPronunciationReading({ grapheme, pinyin })) {
    return true;
  }
  if (reading.isMain) {
    return true;
  }
  const level = script === "simplified" ? reading.hsk2026Level : reading.tbclLevel;
  return curriculumGrade !== null && level !== null && level <= curriculumGrade;
}
